import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PREDICTIONS_QUERY_KEYS } from "@/features/predictions/constants/queryKeys";
import { preferencesService } from "@/features/predictions/services/preferences.service";
import type {
  MyPronosticsPreferences,
  PreferencePatch,
} from "@/features/predictions/types/preferences.type";

type MutationContext = { previous: MyPronosticsPreferences | undefined };

export const usePreferencesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<MyPronosticsPreferences, Error, PreferencePatch, MutationContext>({
    mutationFn: (patch) => preferencesService.updatePreferences(patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: PREDICTIONS_QUERY_KEYS.myPreferences() });
      const previous = queryClient.getQueryData<MyPronosticsPreferences>(
        PREDICTIONS_QUERY_KEYS.myPreferences(),
      );
      if (previous) {
        queryClient.setQueryData<MyPronosticsPreferences>(
          PREDICTIONS_QUERY_KEYS.myPreferences(),
          { ...previous, ...patch },
        );
      }
      return { previous };
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PREDICTIONS_QUERY_KEYS.myPreferences(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PREDICTIONS_QUERY_KEYS.myPreferences() });
    },
  });
};
